// Copyright © 2019-2026 Focal Systems. All rights reserved.

/**
 * The Action Tool's task card contract, mirrored for the back-office editor.
 *
 * The custom task tables store the card's behaviour in free-form JSON columns, so the generated
 * OpenAPI types widen them to `Record<string, never>[]`. These declarations restore the real
 * shapes — they track `libs/tasks/src/lib/data/TaskCardSettings.ts` in web-focal-os.
 *
 * Every editor keeps unknown keys on the objects it edits: the Action Tool adds keys to these
 * blobs faster than the back office learns about them, and dropping one on a round-trip would
 * silently change how a task behaves in the field.
 */

/** How the coloured card header renders its title. */
export const HEADER_MODES = ["productName", "location", "text", "fullTaskType", "shortTaskType", "template"] as const;
export type HeaderMode = (typeof HEADER_MODES)[number];

/** How the strip below the header renders, if at all. */
export const SUB_HEADER_MODES = ["hidden", "location", "text", "template"] as const;
export type SubHeaderMode = (typeof SUB_HEADER_MODES)[number];

/** How a field's raw value is formatted on the card. */
export const FIELD_TYPES = [
  "text",
  "int",
  "float",
  "float1",
  "float2",
  "percent",
  "percent1",
  "percent2",
  "percent100",
  "duration",
  "currency",
  "currency2",
  "date",
  "dateDmy",
  "time",
  "units",
  "cases",
  "caseCapacity",
  "caseUnits",
  "caseCapacityCaseOrUnits",
  "dateTime",
  "space",
  "localizedText",
] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

export const FIELD_VARIANTS = ["standard", "outlined"] as const;
export type FieldVariant = (typeof FIELD_VARIANTS)[number];

export const FIELD_COLORS = ["info", "error", "warning", "success", "primary", "secondary"] as const;
export type FieldColor = (typeof FIELD_COLORS)[number];

/** Content types a card tab can render. */
export const TAB_CONTENT_TYPES = [
  "image",
  "location",
  "planogram",
  "info",
  "pog_old",
  "pog_new",
  "discrepancy_image",
  "raw_planogram",
  "issues",
  "bay_image",
  "interactiveTemplate",
  "interactivePog",
  "imageSlider",
  "imageWithOverlay",
  "contentSlider",
] as const;
export type TabContentType = (typeof TAB_CONTENT_TYPES)[number];

export const ACTION_SUPPRESSIONS = ["NONE", "EOD", "ONE_DAY", "TWO_DAYS"] as const;
export type ActionSuppression = (typeof ACTION_SUPPRESSIONS)[number];

/** A row in the card's Info tab. */
export interface InfoTabField {
  type: FieldType;
  /** Dotted path into the task object, e.g. `item.shelf` or `custom.rotation.tag_names`. */
  field: string;
  /** i18n key the Action Tool resolves against its `tasks` namespace. */
  label: string;
  variant?: FieldVariant;
  color?: FieldColor;
  /** Chain filter — a `!`-prefixed entry excludes that chain instead. */
  chains?: string[];
  /** Render this field's value as the section header rather than the UPC. */
  isHeader?: boolean;
  [key: string]: unknown;
}

/** A chip in the block directly under the card header. */
export interface TopContentField {
  type: FieldType;
  field: string;
  label: string;
  color?: FieldColor;
  variant?: FieldVariant;
  /** 12-column grid width; 6 gives a half-width chip. */
  gridSize?: number;
  chains?: string[];
  /** Feature flag name; a `!` prefix shows the field when the flag is OFF. */
  featureFlag?: string;
  scope?: "taskCard" | "reverseScanDrawer";
  [key: string]: unknown;
}

/** A tab is either a bare content type or an object configuring one. */
export interface TaskCardTabObject {
  name: string;
  contentType: TabContentType;
  tabLabels?: { abbr: string; default: string };
  overlayVisible?: boolean;
  zoomable?: boolean;
  imageSliderConfig?: Record<string, unknown>;
  [key: string]: unknown;
}
export type TaskCardTab = TabContentType | string | TaskCardTabObject;

/** A worked / not-worked button on the card's action bar. */
export interface TaskActionConfig {
  type: string;
  displayName: string;
  suppression?: ActionSuppression;
  label?: string;
  icon?: string;
  color?: string;
  trigger?: string;
  triggerContext?: Record<string, unknown>;
  subActions?: TaskActionConfig[];
  inventoryUpdateRequired?: boolean;
  allocationUpdateRequired?: boolean;
  psiRequired?: boolean;
  confirmationRequired?: boolean;
  confirmationMessage?: string;
  confirmationPayload?: Record<string, unknown>;
  skipDirtyCheck?: boolean;
  [key: string]: unknown;
}

/** A button that starts a guided flow instead of resolving the task outright. */
export interface TaskStartActionConfig {
  displayName: string;
  resumeDisplayName?: string;
  color?: string;
  resumeColor?: string;
  variant?: "outlined" | "contained" | "text";
  icon?: string;
  trigger?: string;
  localAssign?: boolean;
  triggerContext?: Record<string, unknown>;
  apiCall?: Record<string, unknown>;
  [key: string]: unknown;
}

/** One entry in the snooze picker. */
export interface SnoozeConfig {
  displayName: string;
  minutes: number;
  [key: string]: unknown;
}

/** Colours of the location pill under the header. */
export interface LocationStatusConfig {
  color?: string | null;
  textColor?: string | null;
}

/** The complete card, as `/v1/tasks/{id}/card` returns and accepts it. */
export interface TaskCardSettings {
  // Identity
  fullName: string;
  shortName?: string | null;
  acronym?: string | null;
  isPickable: boolean;
  algoFillAction: string;
  serviceUserId?: number | null;
  // Actions
  startActions: TaskStartActionConfig[];
  workedActions: TaskActionConfig[];
  notWorkedActions: TaskActionConfig[];
  snoozeOptions: SnoozeConfig[];
  // Colours and header
  color: string;
  headerFontColor: string;
  headerMode: HeaderMode;
  headerText?: string | null;
  subHeaderMode?: SubHeaderMode | null;
  subHeaderText?: string | null;
  subHeaderColor?: string | null;
  subHeaderFontColor?: string | null;
  icon?: string | null;
  locationStatus: LocationStatusConfig;
  // Behaviour toggles
  alwaysShowOosImage: boolean;
  showProductImages: boolean;
  alwaysExpandItemInfo: boolean;
  enableSnoozeButton: boolean;
  enableSnoozeSetTimeButton: boolean;
  enableCompress: boolean;
  enableInventoryButton: boolean;
  enableDeleteTaskButton: boolean;
  disableActionAutoselect: boolean;
  // Content
  tabs: TaskCardTab[];
  infoTabFields: InfoTabField[];
  topContentFields: TopContentField[];
}

/**
 * Card fields that have no column on the override tables, so they can only be edited globally.
 * Mirrors what `CustomTaskDefinition.Overrides` / `CustomTaskAppearance.Overrides` leave out.
 */
export const GLOBAL_ONLY_FIELDS = ["startActions"] as const;

/** The content type a tab entry renders, whichever of the two forms it takes. */
export function tabContentType(tab: TaskCardTab): string {
  return typeof tab === "string" ? tab : tab.contentType;
}

/** The stable key a tab entry is identified by. */
export function tabKey(tab: TaskCardTab): string {
  return typeof tab === "string" ? tab : tab.name;
}
